import { IVIMPictrogram } from '_store/api/types';
import { useLocalization } from '@fluent/react';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import React, { useEffect,useState } from 'react';

import { PictogramFullID } from '../../../icons/ivim/PictogramFulID';
import { PictogramImage } from '../../../icons/ivim/PictogramImage';
import styles from '../styles';
import { TablePaginationActions } from './TablePaginationActions';

interface PictogramsTableProps {
  rows: IVIMPictrogram[];
  rowSelected?: string;
  selectPictogram: (row: IVIMPictrogram) => void;
}

export const PictogramsTable = ({ rows, rowSelected, selectPictogram }: PictogramsTableProps) => {
  const classes = styles();
  const { l10n } = useLocalization();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Reset pagination to first page when data changes
  useEffect(() => {
    setPage(0);
  }, [rows]);

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>ID</TableCell>
            <TableCell>{l10n.getString('forms-ivim-pictogram-name')}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : rows
          ).map((row) => {
            const borderStyle = row.description === rowSelected ? `3px solid ${green[500]}` : '';

            return (
              <TableRow
                key={row.description}
                style={{
                  borderLeft: borderStyle,
                }}
              >
                <TableCell component="th" scope="row">
                  <PictogramImage
                    pictogramNature={row.pictogramCategoryCode}
                    pictogramSerialNumber={row.pictogramSerialNumber}
                    serviceSubCategoryCode={row.serviceSubCategoryCode}
                    width={25}
                    style={{ margin: 0 }}
                  />
                </TableCell>
                <TableCell component="th" scope="row">
                  <PictogramFullID
                    serviceCategoryCode={row.serviceCategoryCode}
                    serviceSubCategoryCode={row.serviceSubCategoryCode}
                    pictogramCategoryCode={row.pictogramCategoryCode}
                    pictogramSerialNumber={row.pictogramSerialNumber}
                  />
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.description}
                </TableCell>
                <TableCell className={classes.tableCellRight}>
                  {row.description === rowSelected ? (
                    <Chip
                      variant="outlined"
                      icon={<FontAwesomeIcon icon={faCheck} style={{ color: green[500] }} />}
                      label={l10n.getString('forms-ivim-pictogram-selected')}
                      className={classes.chip}
                      size="small"
                    />
                  ) : (
                    <Tooltip
                      title={l10n.getString('forms-ivim-pictogram-select')}
                      placement={'left'}
                    >
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        onClick={() => selectPictogram(row)}
                        style={{ cursor: 'pointer' }}
                        size="lg"
                      />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={2} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: l10n.getString('table-all'), value: -1 }]}
              colSpan={3}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': l10n.getString('table-rows-per-page') },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={l10n.getString('table-rows-per-page')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${l10n.getString('table-of')} ${count}`
              }
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};
